import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { formatDecimalHours } from "../../utils/timeUtils";

export default function MonthlyAttendanceCalendar() {
  const { month, year } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [showMarkDialog, setShowMarkDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [reachingTime, setReachingTime] = useState("");
  const [marking, setMarking] = useState(false);
  const [staffType, setStaffType] = useState("Task-Based");
  const [timeTrackingStatus, setTimeTrackingStatus] = useState({});
  const [confirmAttendance, setConfirmAttendance] = useState({ open: false, status: null, timeValue: null });
  const [confirmTimeTracking, setConfirmTimeTracking] = useState({ open: false, action: null, endpoint: null, message: null, successMessage: null });
  const [showTimeTrackingDialog, setShowTimeTrackingDialog] = useState(false);
  const [boutiqueSchedule, setBoutiqueSchedule] = useState([]);
  const [scheduleMap, setScheduleMap] = useState({});

  useEffect(() => {
    fetchMonthAttendance();
    fetchBoutiqueSchedule();
  }, [month, year]);

  const fetchMonthAttendance = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/staff/my-attendance/records-with-tracking`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const records = data.records || [];
        setStaffType(data.stats?.staffType || "Task-Based");
        
        // Filter records for the selected month/year
        const filteredRecords = records.filter((record) => {
          const recordDate = new Date(record.date);
          return (
            recordDate.getMonth() + 1 === parseInt(month) &&
            recordDate.getFullYear() === parseInt(year)
          );
        });

        setAttendanceRecords(filteredRecords);

        // Create a map for quick lookup
        const map = {};
        filteredRecords.forEach((record) => {
          const day = new Date(record.date).getDate();
          map[day] = record;
        });
        setAttendanceMap(map);
        
        // If hourly staff, fetch today's time tracking status
        if (data.stats?.staffType === "Hourly") {
          await fetchTimeTrackingStatus();
        }
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeTrackingStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/time-tracking/today-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTimeTrackingStatus(data);
      }
    } catch (error) {
      console.error("Error fetching time tracking status:", error);
    }
  };

  const fetchBoutiqueSchedule = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/schedule/${year}/${month}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBoutiqueSchedule(data.dates || []);
        
        // Create a map for quick lookup
        const map = {};
        (data.dates || []).forEach((entry) => {
          const date = new Date(entry.Date);
          const day = date.getDate();
          map[day] = entry.DayType;
        });
        setScheduleMap(map);
      }
    } catch (error) {
      console.error("Error fetching boutique schedule:", error);
    }
  };

  const getDaysInMonth = () => {
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(parseInt(year), parseInt(month) - 1, 1).getDay();
  };

  const getMonthName = () => {
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Present":
        return "#4CAF50";
      case "Absent":
        return "#F44336";
      case "Late":
        return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  const getStatusLightColor = (status) => {
    switch (status) {
      case "Present":
        return "#E8F5E9";
      case "Absent":
        return "#FFEBEE";
      case "Late":
        return "#FFF3E0";
      default:
        return "#F5F5F5";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Present":
        return "P";
      case "Absent":
        return "A";
      case "Late":
        return "L";
      default:
        return "";
    }
  };

  const formatTimeOnly = (timeString) => {
    if (!timeString) return "";
    // If it's already in HH:MM format, return as is
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    // If it's in HH:MM:SS format, extract HH:MM
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
      return timeString.substring(0, 5);
    }
    // Try to parse as time and format
    try {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
    } catch (e) {
      console.error('Error formatting time:', e);
    }
    return timeString;
  };

  const handleDayClick = (day) => {
    const attendance = attendanceMap[day];
    const dayType = scheduleMap[day];
    const today = new Date();
    const clickedDate = new Date(parseInt(year), parseInt(month) - 1, day);
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Check if attendance already marked - just return silently, buttons will handle interaction
    if (attendance) {
      // If it's today and hourly staff, show time tracking options
      if (clickedDate.getTime() === todayDate.getTime() && staffType === "Hourly" && (attendance.status === "Present" || attendance.status === "Late")) {
        setSelectedDay(day);
        setShowTimeTrackingDialog(true);
        return;
      }
      return;
    }
    
    // Check if it's a holiday
    if (dayType === 'Holiday') {
      toast.error("Cannot mark attendance on a holiday");
      return;
    }
    
    // Check if trying to mark future date
    if (clickedDate > todayDate) {
      toast.error("Cannot mark attendance for future dates");
      return;
    }
    
    // Check if trying to mark past date (not today)
    if (clickedDate < todayDate) {
      toast.error("Cannot mark attendance for past dates. Only today's attendance can be marked.");
      return;
    }
    
    // If it's today and not marked, show dialog
    // Auto-fill reaching time with current time
    const currentTime = new Date();
    const hours = String(currentTime.getHours()).padStart(2, '0');
    const minutes = String(currentTime.getMinutes()).padStart(2, '0');
    setReachingTime(`${hours}:${minutes}`);
    
    setSelectedDay(day);
    setShowMarkDialog(true);
  };

  const handleCloseDialog = () => {
    setShowMarkDialog(false);
    setSelectedDay(null);
    setReachingTime("");
  };

  const showAttendanceConfirmation = (status, timeValue = null) => {
    // Validate reaching time for Late status before showing confirmation
    if (status === "Late") {
      if (!timeValue) {
        toast.error("Reaching time is required for Late status");
        return;
      }
      // Validate time format
      if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeValue)) {
        toast.error("Invalid time format. Please use HH:MM format");
        return;
      }
    }
    setConfirmAttendance({ open: true, status, timeValue });
  };

  const markAttendance = async (status, timeValue = null) => {
    try {
      setConfirmAttendance({ open: false, status: null, timeValue: null });
      setMarking(true);
      const token = localStorage.getItem("token");

      // For hourly staff, use punch-in endpoint for Present/Late
      if (staffType === "Hourly" && (status === "Present" || status === "Late")) {
        const punchInResponse = await fetch(
          `${API_BASE_URL}/api/time-tracking/punch-in`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              status,
              reachingTime: status === "Late" ? timeValue : null,
            }),
          }
        );

        const data = await punchInResponse.json();

        if (punchInResponse.ok) {
          toast.success("Punched in successfully!");
          await fetchTimeTrackingStatus();
        } else {
          toast.error(data.message || "Failed to punch in");
        }
      } else {
        // Regular attendance marking for task-based staff or absent status
        const response = await fetch(
          `${API_BASE_URL}/api/staff/mark-attendance`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status, reachingTime: timeValue }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          toast.success(`Attendance marked as ${status}!`);
        } else {
          toast.error(data.message || "Failed to mark attendance");
        }
      }

      handleCloseDialog();
      fetchMonthAttendance(); // Refresh data
    } catch (error) {
      console.error("Error marking attendance:", error);
      toast.error("Failed to mark attendance");
    } finally {
      setMarking(false);
    }
  };

  const showTimeTrackingConfirmation = (action, endpoint, message, successMessage) => {
    setConfirmTimeTracking({ open: true, action, endpoint, message, successMessage });
  };

  const handleTimeTrackingAction = async () => {
    const { action, endpoint, successMessage } = confirmTimeTracking;
    try {
      setConfirmTimeTracking({ open: false, action: null, endpoint: null, message: null, successMessage: null });
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/time-tracking/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(successMessage);
        await fetchTimeTrackingStatus();
        await fetchMonthAttendance();
      } else {
        toast.error(data.message || `Failed to ${action}`);
      }
    } catch (error) {
      console.error(`Error ${action}:`, error);
      toast.error(`Failed to ${action}`);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            border: "1px solid #E0E0E0",
            bgcolor: "#FAFAFA",
            borderRadius: { xs: 1, sm: 1.5 },
            minHeight: "inherit",
            maxHeight: "inherit",
          }}
        />
      );
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const attendance = attendanceMap[day];
      const hasAttendance = !!attendance;
      const dayType = scheduleMap[day];

      // Check if this day is a Sunday (0 = Sunday)
      const dayDate = new Date(parseInt(year), parseInt(month) - 1, day);
      const isSunday = dayDate.getDay() === 0;
      
      // Determine if this is a holiday or opened Sunday
      const isHoliday = dayType === 'Holiday' || (isSunday && !dayType);
      const isOpenedSunday = dayType === 'OpenSunday' && isSunday;
      const isRegularSunday = isSunday && !isOpenedSunday && !dayType;

      // Check if this is today
      const today = new Date();
      const isToday = 
        day === today.getDate() &&
        parseInt(month) === today.getMonth() + 1 &&
        parseInt(year) === today.getFullYear();
      
      // Only show time tracking buttons if it's today, hourly staff, has attendance, NOT a holiday, and NOT a regular Sunday
      const showTimeTrackingButtons = isToday && staffType === "Hourly" && hasAttendance && attendance && (attendance.status === "Present" || attendance.status === "Late") && !isHoliday && !isRegularSunday;

      // Determine background color
      let bgColor = "#F5F5F5"; // Default working day
      if (isHoliday || isRegularSunday) {
        bgColor = "#FFE5E5"; // Holiday color
      } else if (isOpenedSunday) {
        bgColor = "#FFF9C4"; // Opened Sunday color
      } else if (hasAttendance) {
        bgColor = getStatusLightColor(attendance.status);
      }

      days.push(
        <Box
          key={day}
          onClick={() => !(isRegularSunday || isHoliday) && handleDayClick(day)}
          sx={{
            border: "1px solid #E0E0E0",
            bgcolor: bgColor,
            borderRadius: { xs: 1, sm: 1.5 },
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minHeight: "inherit",
            maxHeight: "inherit",
            p: { xs: 0.25, sm: 0.5, md: 0.75 },
            transition: "all 0.2s",
            cursor: (isHoliday || isRegularSunday) ? "not-allowed" : "pointer",
            opacity: (isHoliday || isRegularSunday) ? 0.6 : 1,
            "&:hover": {
              opacity: (isHoliday || isRegularSunday) ? 0.6 : 0.9,
              transform: (isHoliday || isRegularSunday) ? "none" : "scale(1.02)",
              boxShadow: (isHoliday || isRegularSunday) ? 0 : 2,
            },
          }}
        >
          {/* Day Number */}
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: { xs: 2, sm: 4, md: 6 },
              left: { xs: 2, sm: 4, md: 6 },
              fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.875rem" },
              fontWeight: 600,
              color: "#666",
              zIndex: 1,
            }}
          >
            {day}
          </Typography>

          {/* Content Container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: { xs: 1.5, sm: 2, md: 2.5 },
              gap: { xs: 0.25, sm: 0.5 },
            }}
          >

            {(isHoliday || isRegularSunday) && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#D32F2F",
                    fontWeight: 700,
                    fontSize: { xs: "0.45rem", sm: "0.55rem", md: "0.65rem" },
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  {dayType === 'Holiday' ? 'HOLIDAY' : 'SUNDAY'}
                </Typography>
              </>
            )}

            {isOpenedSunday && !hasAttendance && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#f57c00",
                    fontWeight: 700,
                    fontSize: { xs: "0.45rem", sm: "0.55rem", md: "0.65rem" },
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  OPEN
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#f57c00",
                    fontWeight: 500,
                    fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                    lineHeight: 1,
                  }}
                >
                  Optional
                </Typography>
              </>
            )}

            {!isHoliday && !isRegularSunday && hasAttendance && (
              <>
                {/* Status Badge */}
                <Box
                  sx={{
                    width: { xs: 16, sm: 24, md: 32, lg: 36 },
                    height: { xs: 16, sm: 24, md: 32, lg: 36 },
                    borderRadius: { xs: 1, sm: 1.5 },
                    bgcolor: getStatusColor(attendance.status),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 0.25, sm: 0.5 },
                  }}
                >
                  <Typography
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: { xs: "0.6rem", sm: "0.8rem", md: "1rem", lg: "1.1rem" },
                      lineHeight: 1,
                    }}
                  >
                    {getStatusLabel(attendance.status)}
                  </Typography>
                </Box>

                {/* Time Information */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: { xs: 0.1, sm: 0.2 },
                    width: "100%",
                  }}
                >
                  {staffType === "Hourly" && (
                    <>
                      {attendance.punchInTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#4CAF50",
                            fontSize: { xs: "0.3rem", sm: "0.4rem", md: "0.5rem" },
                            fontWeight: 600,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          In: {formatTimeOnly(attendance.punchInTime)}
                        </Typography>
                      )}
                      {attendance.punchOutTime && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#F44336",
                            fontSize: { xs: "0.3rem", sm: "0.4rem", md: "0.5rem" },
                            fontWeight: 600,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          Out: {formatTimeOnly(attendance.punchOutTime)}
                        </Typography>
                      )}
                      {attendance.totalWorkHours > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#1976D2",
                            fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                            fontWeight: 700,
                            lineHeight: 1,
                            textAlign: "center",
                          }}
                        >
                          {formatDecimalHours(attendance.totalWorkHours)}
                        </Typography>
                      )}
                    </>
                  )}

                  {staffType === "Task-Based" && attendance.reachingTime && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.55rem" },
                        fontWeight: 600,
                        lineHeight: 1,
                        textAlign: "center",
                      }}
                    >
                      {formatTimeOnly(attendance.reachingTime)}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      );
    }

    return days;
  };

  const calculateStats = () => {
    const present = attendanceRecords.filter((r) => r.status === "Present").length;
    const absent = attendanceRecords.filter((r) => r.status === "Absent").length;
    const late = attendanceRecords.filter((r) => r.status === "Late").length;
    
    // Calculate working days using boutique schedule
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    let workingDays = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(parseInt(year), parseInt(month) - 1, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday
      const dayType = scheduleMap[day];
      
      // If marked as Holiday, skip
      if (dayType === 'Holiday') {
        continue;
      }
      
      // If it's a Sunday
      if (dayOfWeek === 0) {
        // Only count if marked as OpenSunday
        if (dayType === 'OpenSunday') {
          workingDays++;
        }
      } else {
        // Weekday - count as working day
        workingDays++;
      }
    }
    
    return { present, absent, late, total: workingDays };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            bgcolor: "#1a1a1a",
          }}
        >
          <CircularProgress sx={{ color: "#C59A58" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 }, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 2, md: 3 }, flexWrap: "wrap", gap: 1 }}>
          <IconButton
            onClick={() => navigate("/my-attendance-salary")}
            sx={{
              mr: 2,
              bgcolor: "#8B7355",
              color: "white",
              "&:hover": { bgcolor: "#6D5A43" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: "#8B7355",
              fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            }}
          >
            {getMonthName()} - Attendance Calendar
          </Typography>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#E3F2FD", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1976D2" }}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#E8F5E9", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#4CAF50" }}>
                  {stats.present}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Present
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card sx={{ bgcolor: "#FFEBEE", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#F44336" }}>
                  {stats.absent}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Absent
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: "wrap" }}>
          <Chip
            label="P - Present"
            sx={{ bgcolor: "#4CAF50", color: "white", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
          />
          <Chip
            label="A - Absent"
            sx={{ bgcolor: "#F44336", color: "white", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
          />
        </Box>

        {/* Calendar */}
        <Card sx={{ boxShadow: 3, overflow: 'hidden', width: '100%' }}>
          <CardContent sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>
            {/* Day Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: { xs: 0.5, sm: 1 },
                mb: { xs: 0.5, sm: 1 },
              }}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thurs", "Fri", "Sat"].map((day) => (
                <Box
                  key={day}
                  sx={{
                    textAlign: "center",
                    py: { xs: 0.5, sm: 0.75, md: 1 },
                    bgcolor: "#BC965E",
                    borderRadius: { xs: 1, sm: 1.5 },
                    minHeight: { xs: 24, sm: 32, md: 40 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ 
                      fontWeight: 700, 
                      color: "#ffffff", 
                      fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.875rem" },
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: { xs: 0.5, sm: 1 },
                "& > *": {
                  minHeight: { xs: 80, sm: 100, md: 130, lg: 150 },
                  maxHeight: { xs: 100, sm: 120, md: 150, lg: 180 },
                }
              }}
            >
              {renderCalendar()}
            </Box>
          </CardContent>
        </Card>

        {/* Mark Attendance Dialog */}
        <Dialog
          open={showMarkDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#2a2a2a",
              border: "1px solid #444",
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#C59A58", color: "white", fontWeight: 600 }}>
            Mark Attendance - {selectedDay && `${getMonthName().split(' ')[0]} ${selectedDay}, ${year}`}
          </DialogTitle>
          <DialogContent sx={{ mt: 3, bgcolor: "#2a2a2a" }}>
            <Typography variant="body2" sx={{ mb: 3, color: "#9CA3AF" }}>
              Please mark your attendance for today. Once marked, it cannot be edited.
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => showAttendanceConfirmation("Present")}
                disabled={marking}
                sx={{
                  bgcolor: "#4CAF50",
                  color: "white",
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#45A049" },
                }}
              >
                P - Present
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={() => showAttendanceConfirmation("Absent")}
                disabled={marking}
                sx={{
                  bgcolor: "#F44336",
                  color: "white",
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#E53935" },
                }}
              >
                A - Absent
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#2a2a2a" }}>
            <Button onClick={handleCloseDialog} sx={{ color: "#C59A58" }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Attendance Confirmation Dialog */}
        <Dialog
          open={confirmAttendance.open}
          onClose={() => setConfirmAttendance({ open: false, status: null, timeValue: null })}
          PaperProps={{
            sx: {
              bgcolor: "#2a2a2a",
              border: "1px solid #444",
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#C59A58", color: "white" }}>Confirm Attendance</DialogTitle>
          <DialogContent sx={{ bgcolor: "#2a2a2a" }}>
            <Typography sx={{ color: "#fff" }}>
              Are you sure you want to mark your attendance as <strong>{confirmAttendance.status}</strong>?
              {confirmAttendance.status === 'Late' && confirmAttendance.timeValue && (
                <> (Reaching time: {confirmAttendance.timeValue})</>
              )}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "#9CA3AF" }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ bgcolor: "#2a2a2a" }}>
            <Button 
              onClick={() => setConfirmAttendance({ open: false, status: null, timeValue: null })}
              sx={{ color: '#9CA3AF' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => markAttendance(confirmAttendance.status, confirmAttendance.timeValue)}
              variant="contained"
              sx={{ bgcolor: '#C59A58', '&:hover': { bgcolor: '#A27E4E' } }}
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time Tracking Dialog */}
        <Dialog
          open={showTimeTrackingDialog}
          onClose={() => setShowTimeTrackingDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "#2a2a2a",
              border: "1px solid #444",
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#C59A58", color: "white", fontWeight: 600 }}>
            Time Tracking - {selectedDay && `${getMonthName().split(' ')[0]} ${selectedDay}, ${year}`}
          </DialogTitle>
          <DialogContent sx={{ mt: 3, bgcolor: "#2a2a2a" }}>
            <Typography variant="body2" sx={{ mb: 3, color: "#9CA3AF" }}>
              Select a time tracking action for today.
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {!timeTrackingStatus.hasPunchedOut ? (
                <>
                  {timeTrackingStatus.hasActiveBreak ? (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        setShowTimeTrackingDialog(false);
                        showTimeTrackingConfirmation(
                          "end break",
                          "end-break",
                          "Are you sure you want to end your break?",
                          "Break ended"
                        );
                      }}
                      sx={{
                        bgcolor: "#FF9800",
                        color: "white",
                        py: 1.5,
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#FB8C00" },
                      }}
                    >
                      End Break
                    </Button>
                  ) : (
                    timeTrackingStatus.hasPunchedIn && !timeTrackingStatus.hasCompletedBreak && (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                          setShowTimeTrackingDialog(false);
                          showTimeTrackingConfirmation(
                            "start break",
                            "start-break",
                            "Are you sure you want to start your break?",
                            "Break started"
                          );
                        }}
                        sx={{
                          bgcolor: "#2196F3",
                          color: "white",
                          py: 1.5,
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#1976D2" },
                        }}
                      >
                        Start Break
                      </Button>
                    )
                  )}
                  {timeTrackingStatus.hasPunchedIn && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        setShowTimeTrackingDialog(false);
                        showTimeTrackingConfirmation(
                          "punch out",
                          "punch-out",
                          "Are you sure you want to punch out? This will end your work day.",
                          "Punched out successfully"
                        );
                      }}
                      sx={{
                        bgcolor: "#F44336",
                        color: "white",
                        py: 1.5,
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#E53935" },
                      }}
                    >
                      Punch Out
                    </Button>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="h6" sx={{ color: "#4CAF50", mb: 1 }}>
                    Work Day Complete
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9CA3AF" }}>
                    You have already punched out for today.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "#2a2a2a" }}>
            <Button onClick={() => setShowTimeTrackingDialog(false)} sx={{ color: "#C59A58" }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Time Tracking Confirmation Dialog */}
        <Dialog
          open={confirmTimeTracking.open}
          onClose={() => setConfirmTimeTracking({ open: false, action: null, endpoint: null, message: null, successMessage: null })}
          PaperProps={{
            sx: {
              bgcolor: "#2a2a2a",
              border: "1px solid #444",
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: "#C59A58", color: "white" }}>Confirm Action</DialogTitle>
          <DialogContent sx={{ bgcolor: "#2a2a2a" }}>
            <Typography sx={{ color: "#fff" }}>
              {confirmTimeTracking.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ bgcolor: "#2a2a2a" }}>
            <Button 
              onClick={() => setConfirmTimeTracking({ open: false, action: null, endpoint: null, message: null, successMessage: null })}
              sx={{ color: '#9CA3AF' }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTimeTrackingAction}
              variant="contained"
              sx={{ bgcolor: '#C59A58', '&:hover': { bgcolor: '#A27E4E' } }}
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
